import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lightbulb, X, Flag, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface HintSystemProps {
  solution: string;
  guesses: string[];
  onHintUsed: (position: number, letter: string) => void;
  hintsUsed: number;
  maxHints?: number;
  onGiveUp?: () => void;
}

export function HintSystem({ 
  solution, 
  guesses, 
  onHintUsed, 
  hintsUsed, 
  maxHints = 3,
  onGiveUp
}: HintSystemProps) {
  const [currentHint, setCurrentHint] = useState<string | null>(null);
  const [showGiveUpDialog, setShowGiveUpDialog] = useState(false);

  const generateHint = () => {
    if (hintsUsed >= maxHints) {
      setCurrentHint("Maximum hints reached (3/3)");
      return;
    }

    // Find which positions already have correct (green) letters
    const correctPositions = new Set<number>();
    guesses.forEach((guess, guessIndex) => {
      for (let i = 0; i < guess.length; i++) {
        if (guess[i].toLowerCase() === solution[i].toLowerCase()) {
          correctPositions.add(i);
        }
      }
    });

    // Try positions from 5th to 1st (index 4 to 0)
    const positions = [4, 3, 2, 1, 0]; // 5th, 4th, 3rd, 2nd, 1st position
    let hintPosition = -1;
    let hintsSkipped = 0;

    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      // Skip if this position is already correct or doesn't exist in the word
      if (pos >= solution.length || correctPositions.has(pos)) {
        hintsSkipped++;
        continue;
      }
      
      // Check if this is the hint we should give based on hintsUsed
      if (i - hintsSkipped === hintsUsed) {
        hintPosition = pos;
        break;
      }
    }

    if (hintPosition >= 0) {
      const positionName = ["1st", "2nd", "3rd", "4th", "5th"][hintPosition];
      const letter = solution[hintPosition].toUpperCase();
      setCurrentHint(`The ${positionName} letter is "${letter}"`);
      onHintUsed(hintPosition, letter);
    } else {
      // No more positions available to hint
      setCurrentHint("All available letters already revealed!");
    }
  };

  const closeHint = () => setCurrentHint(null);

  const handleGiveUpConfirm = () => {
    setShowGiveUpDialog(false);
    if (onGiveUp) {
      onGiveUp();
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <Button
        variant="default"
        onClick={generateHint}
        disabled={hintsUsed >= maxHints}
        className="h-10 w-full text-sm bg-amber-600 hover:bg-amber-700 text-white"
      >
        <Lightbulb className="h-4 w-4 mr-2" />
        Get a Hint ({hintsUsed}/{maxHints})
      </Button>
      
      {onGiveUp && (
        <Button
          variant="outline"
          onClick={() => setShowGiveUpDialog(true)}
          className="h-9 w-full text-sm text-muted-foreground hover:text-destructive hover:border-destructive"
        >
          <Flag className="h-4 w-4 mr-2" />
          Give Up
        </Button>
      )}

      {currentHint && (
        <Card className="p-3 w-full max-w-sm animate-slide-in relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeHint}
            className="absolute top-1 right-1 h-6 w-6 p-0"
            aria-label="Close hint"
          >
            <X className="h-3 w-3" />
          </Button>
          <p className="text-sm text-center pr-6">{currentHint}</p>
        </Card>
      )}

      <AlertDialog open={showGiveUpDialog} onOpenChange={setShowGiveUpDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Give Up on This Puzzle?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to give up? This action will:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Count as a completed game (loss)</li>
                <li>End your current winning streak</li>
                <li>Show you the solution</li>
                <li>Mark this puzzle as completed</li>
              </ul>
              <p className="font-medium pt-2">
                This cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleGiveUpConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Yes, Give Up
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}